<template>
  <div class="h-full overflow-auto bg-neutral-100">
    <div class="mb-20">
      <div
        class="mx-auto mt-10 sm:px-5 md:px-8 columns-1 xl:columns-2 3xl:columns-3 gap-10 leading-6 [column-fill:balance]"
      >
        <LandingCard title="Electronic Forms" :view-all-to="{ name: routeNames.electronicForm.list }">
          <div
            v-if="isGetElectronicFormsSuccess && electronicForms?._items?.length === 0"
            class="bg-neutral-50 p-10 sm:rounded-b-lg"
          >
            <NoneFoundFlag label="No Draft Forms" />
          </div>
          <LandingCardSkeleton v-if="isGetElectronicFormsPending" />
          <LandingCardEntityList title="Submission Pending" :items="submissionPendingForms">
            <template #default="{ item }">
              <LandingCardEntityListItem>
                <template #primaryLabel>
                  <LookupDescription :murn="item.formMurn" />
                  &ndash;
                  <LookupDescription :murn="item.typeMurn" />
                </template>
                <template #secondaryLabel>
                  <LookupDescription :murn="item.groupMurn" />
                </template>
                <template #additionalInfo>
                  <div
                    class="text-sm text-neutral-400 mt-1.5 md:self-center md:mt-0 md:text-right md:pr-4"
                    v-if="item.updatedDateTime && item.updatedBy"
                  >
                    <span class="md:block"
                      >Updated
                      <span class="text-neutral-500">{{
                        tzDate.shortDateTimeString(item.updatedDateTime.toString())
                      }}</span></span
                    >
                    by <span class="text-neutral-500">{{ item.updatedBy.name }}</span>
                  </div>
                </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
          <LandingCardEntityList title="Draft" :items="draftForms">
            <template #default="{ item }">
              <LandingCardEntityListItem>
                <template #primaryLabel>
                  <LookupDescription :murn="item.formMurn" />
                  &ndash;
                  <LookupDescription :murn="item.typeMurn" />
                </template>
                <template #secondaryLabel>
                  <LookupDescription :murn="item.groupMurn" />
                </template>
                <template #additionalInfo>
                  <div
                    class="text-sm text-neutral-400 mt-1.5 md:self-center md:mt-0 md:text-right md:pr-4"
                    v-if="item.updatedDateTime && item.updatedBy"
                  >
                    <span class="md:block"
                      >Updated
                      <span class="text-neutral-500">{{
                        tzDate.shortDateTimeString(item.updatedDateTime.toString())
                      }}</span></span
                    >
                    by <span class="text-neutral-500">{{ item.updatedBy.name }}</span>
                  </div>
                </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
        </LandingCard>

        <LandingCard title="Online Services">
          <LandingCardLinkSections label="Stationary Source Permitting, Compliance, and Source Testing">
            <LandingCardLinkSection
              link-label="Apply for a New Stationary Source Permit"
              :to="{ name: routeNames.electronicForm.stationarySourcePermittingSelect }"
            />
            <LandingCardLinkSection
              link-label="Request Access to a Stationary Source"
              description="If you need to revise, transfer, or renew an existing stationary source permit or you need to submit compliance or source testing forms, request access to your stationary source."
              :to="{ name: routeNames.accessRequest.stationarySource }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Dust Control Permitting">
            <!-- :to="{ name: routeNames.electronicForm.dustControlPermittingSelect }" -->
            <LandingCardLinkSection
              link-label="Apply for a New Dust Control Permit"
              :to="{ name: routeNames.notImplemented }"
            />
            <LandingCardLinkSection
              link-label="Request Access to a Construction Project"
              description="If you need to revise, transfer, renew, or close an existing dust control permit, request access to your construction project."
              :to="{ name: routeNames.accessRequest.constructionProject }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Dust Classes and Dust Monitor Classes">
            <LandingCardLinkSection link-label="Register for a Dust Class" :to="{ name: routeNames.notImplemented }" />
            <LandingCardLinkSection
              link-label="Register for a Dust Monitor Class"
              :to="{ name: routeNames.notImplemented }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Enforcement">
            <LandingCardLinkSection
              link-label="Request hearing with the Air Pollution Control Hearing Board"
              :to="{ name: routeNames.notImplemented }"
            />
            <LandingCardLinkSection
              link-label="Appeal Hearing Officer Decision"
              :to="{ name: routeNames.notImplemented }"
            />
            <LandingCardLinkSection
              link-label="Apply for an Olive Tree Exemption "
              :to="{ name: routeNames.notImplemented }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Asbestos">
            <LandingCardLinkSection
              link-label="Submit a Notification of Asbestos Abatement"
              :to="{ name: routeNames.electronicForm.ef041.new }"
            />
            <LandingCardLinkSection
              link-label="Submit a Demolition Notification"
              :to="{ name: routeNames.electronicForm.ef042.new }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Open Burning">
            <LandingCardLinkSection
              link-label="Submit Open Burning Application and Notification"
              :to="{ name: routeNames.notImplemented }"
            />
          </LandingCardLinkSections>
        </LandingCard>

        <LandingCard
          title="My Stationary Sources"
          :view-all-to="{ name: routeNames.stationarySource.list }"
          v-if="isGetStationarySourcesSuccess && stationarySources?._items?.length"
        >
          <LandingCardSkeleton v-if="isGetStationarySourcesPending" />
          <LandingCardEntityList :items="stationarySources?._items">
            <!-- title="Active Permits"  -->
            <template #default="{ item }">
              <LandingCardEntityListItem :secondaryLabel="formatAddress(item)">
                <template #primaryLabel> {{ item.name }} &ndash; {{ item.number }} </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
        </LandingCard>

        <LandingCard
          title="My Construction Projects"
          :view-all-to="{ name: routeNames.constructionProject.list }"
          v-if="isGetConstructionProjectsSuccess && constructionProjects?._items?.length"
        >
          <div
            v-if="isGetConstructionProjectsSuccess && constructionProjects?._items?.length === 0"
            class="bg-neutral-50 p-10 sm:rounded-b-lg"
          >
            <NoneFoundFlag />
          </div>
          <LandingCardSkeleton v-if="isGetConstructionProjectsPending" />
          <LandingCardEntityList :items="constructionProjects?._items">
            <template #default="{ item }">
              <LandingCardEntityListItem :secondaryLabel="item.locationDescription">
                <template #primaryLabel> {{ item.name }} &ndash; {{ item.number }} </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
        </LandingCard>

        <!-- TODO: Add resources -->
        <!-- <LandingCard title="Resources">
          <LandingCardLinkSections label="Temporary Form Access">
            <LandingCardLinkSection
              link-label="Forms Development"
              description="Access developed form front-end."
              :to="{ name: routeNames.temporaryFormLanding }"
            />
          </LandingCardLinkSections>
        </LandingCard> -->

        <!-- Questions -->
        <LandingCard>
          <template #default>
            <div class="mx-auto max-w-xl">
              <div class="px-4 pb-7 pt-6">
                <div class="text-center">
                  <h2 class="text-lg font-medium text-neutral-900">Questions?</h2>
                  <p class="mt-1 text-sm text-neutral-500">
                    <span class="text-neutral-700">Tanuja Puvvula</span><br />
                    <span class="leading-loose">
                      <EnvelopeIcon class="mr-2 inline-block h-4 w-4" /><a href="mailto:Tanuja@clarkcountynv.gov"
                        >Tanuja@clarkcountynv.gov</a
                      >
                    </span>
                    <br />
                    <PhoneIcon class="mr-2 inline-block h-4 w-4" />
                    <a href="tel:+1-775-687-9467">+1 (702) 273-8428</a>
                  </p>
                </div>
              </div>
            </div>
          </template>
        </LandingCard>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { InitialBadge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnAttribution, NoneFoundFlag, useAppSettingsStore } from "@modernary/mui/components/common";
import { useUser } from "@modernary/mui/utils/authentication";
import { LoginButton, RegisterButton } from "@modernary/mui/utils/authentication/buttons";
import { computed, defineComponent, watch } from "vue";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { PhoneIcon, EnvelopeIcon } from "@heroicons/vue/20/solid";
import type { Paging } from "@modernary/mui/utils/api";
import { useApi } from "@modernary/mui/utils/api";
import { getConstructionProjects, getElectronicForms, getStationarySources } from "@/services/endpoints";
import stationarySourceService from "@/views/stationarySource/stationarySourceService";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import { routeNames } from "@/routes";
import type { StationarySource } from "@/views/stationarySource/stationarySource";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import type { LandingCardEntity } from "@/components/landing/landingCard";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import constructionProjectService from "@/views/constructionProject/constructionProjectService";
import type { ConstructionProject } from "@/views/constructionProject/constructionProject";
import LandingCard from "@/components/landing/LandingCard.vue";
import type { ElectronicForm, ElectronicFormBE } from "@/views/electronicForm/electronicForm";
import electronicFormService from "@/views/electronicForm/electronicFormService";
import { formatAddress, tzDate } from "@modernary/mui/utils/filters";
import { getElectronicFormRouteNameFromItem } from "@/helpers/electronicFormHelpers";
import { LE } from "@/constants/enums";

// Home content for unauth'd users
export default defineComponent({
  name: "HomeExternal",
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();

    const isAuthenticated = computed(() => userRef.value.isAuthenticated);

    /* 
      Landing pages have a lot of unconventional code,
      not a wise place to use as a reference.
    */

    const {
      dataRef: constructionProjects,
      exec: execGetConstructionProjects,
      isStatusPending: isGetConstructionProjectsPending,
      isStatusSuccess: isGetConstructionProjectsSuccess,
    } = useApi(getConstructionProjects, {
      responseAdapter: async (response) => {
        const data = await adaptPagedResponse(response, constructionProjectService.skinnyResponseAdapter);
        data._items.forEach((item: any) => {
          item.to = { name: routeNames.constructionProject.view, params: { id: item.id } };
        });

        return data as Paging<LandingCardEntity<ConstructionProject>>;
      },
    });

    const {
      dataRef: stationarySources,
      exec: execGetStationarySources,
      isStatusPending: isGetStationarySourcesPending,
      isStatusSuccess: isGetStationarySourcesSuccess,
    } = useApi(getStationarySources, {
      responseAdapter: async (response) => {
        const data = await adaptPagedResponse(response, stationarySourceService.responseAdapter);
        data._items.forEach((item: any) => {
          item.to = { name: routeNames.stationarySource.view, params: { id: item.id } };
        });
        return data as Paging<LandingCardEntity<StationarySource>>;
      },
    });

    const {
      dataRef: electronicForms,
      exec: execGetElectronicForms,
      isStatusPending: isGetElectronicFormsPending,
      isStatusSuccess: isGetElectronicFormsSuccess,
    } = useApi(getElectronicForms, {
      responseAdapter: async (response) => {
        const data = (await adaptPagedResponse(
          response,
          electronicFormService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<ElectronicForm>>;
        data._items.forEach((item) => {
          item.to = { name: getElectronicFormRouteNameFromItem(item), params: { id: item.id } };
        });
        return data as Paging<LandingCardEntity<ElectronicForm>>;
      },
    });

    watch(
      isAuthenticated,
      (newValue) => {
        if (newValue) {
          execGetStationarySources();
          execGetConstructionProjects();
          execGetElectronicForms({
            statusMurnIn: [LE.ElectronicFormStatus.Draft, LE.ElectronicFormStatus.SubmissionPending],
            orderBy: [{ propertyName: "updatedDateTime", isDescending: true }],
            take: 7, // TODO: remove this once users are bound to forms
          });
        }
      },
      { immediate: true },
    );

    const draftForms = computed(() => {
      return electronicForms.value?._items.filter((item) => item.statusMurn === LE.ElectronicFormStatus.Draft);
    });
    const submissionPendingForms = computed(() => {
      return electronicForms.value?._items.filter(
        (item) => item.statusMurn === LE.ElectronicFormStatus.SubmissionPending,
      );
    });

    return {
      draftForms,
      submissionPendingForms,
      userRef,
      BaseButton,
      appSettingsStore,
      isGetConstructionProjectsPending,
      isGetConstructionProjectsSuccess,
      constructionProjects,
      isGetStationarySourcesPending,
      isGetStationarySourcesSuccess,
      stationarySources,
      routeNames,
      isGetElectronicFormsPending,
      isGetElectronicFormsSuccess,
      electronicForms,
      formatAddress,
      tzDate,
    };
  },
  components: {
    BaseButton,
    InitialBadge,
    LoginButton,
    RegisterButton,
    ChevronRightIcon,
    MdrnAttribution,
    NoneFoundFlag,
    PhoneIcon,
    EnvelopeIcon,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LandingCard,
    LookupDescription,
  },
});
</script>
